import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { noop } from 'lodash-es';
import { getPlural } from 'client/utils/plural';
import { REVIEWS_MODULE_CREATIVE_ID } from 'site-modules/shared/components/offers-reviews-module/constants';
import { Pagination } from 'site-modules/shared/components/pagination/pagination';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';
import { scrollToElement } from 'client/utils/scroll';
import { ReviewsListItem } from './reviews-list-item';
import { ReviewsSortingSelect, SORTING } from './reviews-sorting-select';

import './reviews-list.scss';

const PAGE_SIZE = 5;
const PAGINATION_NUM_CONFIG_MOBILE = {
  allAvailablePageNum: 3,
  startNumWithoutTruncate: 3,
  middleNumWithoutTruncate: 3,
  endNumWithoutTruncate: 3,
};

const ARROWS_TRACKING_VALUES = {
  prev: 'previous',
  next: 'next',
};

const handleNaNRating = (review1, review2) => {
  if (!Number.isNaN(review1.rating) || !Number.isNaN(review2.rating)) {
    // if there is a real number, then place it first.
    return Number.isNaN(review1.rating) ? 1 : -1;
  }
  // if both ratings are NaN, then use a secondary sort by date (most recent are first).
  return new Date(review2.date) - new Date(review1.date);
};

export const sortReviews = (reviews, sorting) =>
  reviews.sort((r1, r2) => {
    if ([SORTING.HIGHEST_RATED, SORTING.LOWEST_RATED].includes(sorting)) {
      const rating1 = r1.rating;
      const rating2 = r2.rating;

      if (Number.isNaN(rating1) || Number.isNaN(rating2)) {
        return handleNaNRating(r1, r2);
      }

      const sortByRating = sorting === SORTING.HIGHEST_RATED ? rating2 - rating1 : rating1 - rating2;
      if (sortByRating === 0) {
        // if ratings are equal, then use a secondary sort by date (most recent are first).
        return new Date(r2.date) - new Date(r1.date);
      }
      return sortByRating;
    }

    // For SORTING.MOST_RECENT.
    return new Date(r2.date) - new Date(r1.date);
  });

export const ReviewsListUI = ({ totalReviewsNumber, allReviews, isMobile = false }) => {
  const [pageNumber, setPageNumber] = useState(1);
  const [sorting, setSorting] = useState(SORTING.HIGHEST_RATED);

  const onPaginationLinkClick = useCallback((e, number) => {
    setPageNumber(number);
    scrollToElement('reviews-list');
  }, []);
  const onSortingSelectChange = useCallback(({ value }) => {
    EventToolbox.fireTrackAction({
      event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
      event_data: {
        action_category: TrackingConstant.USER_ACTION_CATEGORY,
        action_name: TrackingConstant.ACTION_VIEW_CONTENT,
        subaction_name: 'sort_reviews',
        action_cause: TrackingConstant.ACTION_CAUSE_BUTTON_CLICK,
        creative_id: REVIEWS_MODULE_CREATIVE_ID,
        value,
      },
    });
    setSorting(value);
    setPageNumber(1);
    scrollToElement('reviews-list');
  }, []);

  const displayedReviews = sortReviews([...allReviews], sorting).slice(
    (pageNumber - 1) * PAGE_SIZE,
    pageNumber * PAGE_SIZE
  );

  const totalPageNumber = Math.ceil(allReviews.length / PAGE_SIZE);

  return (
    <div id="reviews-list" className="reviews-list">
      <div className="d-flex align-items-center mb-1">
        <h3 className="header size-16 fw-bold text-cool-gray-30 flex-grow-1">
          {totalReviewsNumber} {getPlural('Review', totalReviewsNumber)}
        </h3>
        <ReviewsSortingSelect onChange={onSortingSelectChange} selectedSorting={sorting} />
      </div>
      <div>
        {displayedReviews.map(({ text, date, rating }) => {
          const key = `${date} ${rating} ${text.slice(0, 8)} ${text.slice(-8)}`;
          return <ReviewsListItem key={key} text={text} date={date} rating={rating} isMobile={isMobile} />;
        })}
      </div>
      {totalPageNumber > 1 && (
        <Pagination
          className={classnames('d-flex justify-content-center align-items-baseline size-16 mt-1_5', {
            'ms-1_5': pageNumber === 1,
            'me-1_5': pageNumber === totalPageNumber,
          })}
          currentNum={pageNumber}
          totalNum={totalPageNumber}
          paginationLinkBuilder={noop}
          linkComponent="button"
          listClassName="d-flex justify-content-center align-items-baseline"
          currentNumLinkClassname="fw-bold px-0_5 mx-0_25 bg-transparent border-0"
          numLinkClassname="num-link px-0_5 mx-0_25 bg-transparent border-0"
          arrowClassName="size-16 mx-0_5 bg-transparent border-0 px-0"
          onArrowLinkClick={onPaginationLinkClick}
          onPageLinkClick={onPaginationLinkClick}
          isTextForMobile={false}
          dataTrackingId="view_more_reviews"
          arrowDataTrackingId="view_more_reviews"
          arrowsDataTrackingValues={ARROWS_TRACKING_VALUES}
          creativeId={REVIEWS_MODULE_CREATIVE_ID}
          numConfig={isMobile ? PAGINATION_NUM_CONFIG_MOBILE : undefined}
        />
      )}
    </div>
  );
};

ReviewsListUI.propTypes = {
  totalReviewsNumber: PropTypes.number.isRequired,
  allReviews: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string.isRequired,
      date: PropTypes.string.isRequired,
      rating: PropTypes.number.isRequired,
    })
  ).isRequired,
  isMobile: PropTypes.bool,
};

export const mapStateToProps = state => ({
  isMobile: state.mobile,
});

export const ReviewsList = connect(mapStateToProps)(ReviewsListUI);
